/**
 * The custom-session wizard's shape and arithmetic — ROUTE_INVENTORY §2.11
 * #35, #36 and #38.
 *
 * Three rules, none of which is a rendering:
 *
 * - which steps EXIST (the Lessons step is dropped entirely, not disabled,
 *   for a user with no lessons);
 * - which of them you may jump to (back only);
 * - how many concepts the session will actually contain once the length cap
 *   is applied to the match count.
 *
 * Pure, and in `src/lib` so it stays that way. The last one is the reason this
 * file exists: "effective size" is two numbers combined, it drives the Review
 * summary, the cap caveat and whether Start is enabled, and every one of those
 * reads as a plain number on screen — the arithmetic behind it is invisible
 * and is exactly what a story cannot pin.
 */

export type StepKey = 'domain' | 'user' | 'lessons' | 'length' | 'review';

export interface StepDef {
  key: StepKey;
  label: string;
}

/** Length caps offered by the wizard, plus the uncapped option. */
export const LENGTH_OPTIONS = [10, 20, 50] as const;

/**
 * §2.11 #35 — the step list, with Lessons present only when there is
 * something to pick.
 *
 * Dropped from the list rather than rendered-and-disabled: a wizard that
 * showed a fourth step and refused to open it would be advertising a filter
 * the user cannot have. It also means the step INDICES shift, which is why
 * every jump goes through a key rather than a hard-coded position.
 */
export function buildSteps(hasLessons: boolean): StepDef[] {
  return [
    { key: 'domain', label: 'Domain' },
    { key: 'user', label: 'Your tags' },
    ...(hasLessons ? [{ key: 'lessons' as const, label: 'Lessons' }] : []),
    { key: 'length', label: 'Length' },
    { key: 'review', label: 'Review' },
  ];
}

/** Index of a step by key, or `-1`. Keys survive the Lessons step vanishing. */
export function indexOfStep(steps: StepDef[], key: StepKey): number {
  return steps.findIndex(s => s.key === key);
}

/**
 * §2.11 #36 — the stepper jumps BACK only.
 *
 * Forward movement goes through Next, so the user cannot land on Review
 * without passing the steps that decide what Review summarises. The current
 * step counts as reachable — clicking where you already are is a no-op, not a
 * blocked action.
 */
export function canJumpTo(targetIndex: number, currentIndex: number): boolean {
  return targetIndex <= currentIndex;
}

/**
 * §2.11 #38 — how many concepts the session will actually contain.
 *
 * `null` while the count is unknown, so the Review summary can shimmer rather
 * than claim zero. `'all'` means no cap: the session is every match.
 */
export function effectiveSessionSize(
  matchCount: number | null,
  length: number | 'all'
): number | null {
  if (matchCount == null) return null;
  return length === 'all' ? matchCount : Math.min(matchCount, length);
}

/**
 * §2.11 #38 — whether the length cap is actually biting.
 *
 * Drives the Review caveat. Strictly greater: a match count that exactly
 * equals the cap is not capped, and telling the user "more concepts match"
 * when none do would send them to widen a filter that is already showing
 * everything.
 */
export function isCappedByLength(
  matchCount: number | null,
  length: number | 'all'
): boolean {
  if (matchCount == null || length === 'all') return false;
  return matchCount > length;
}
